import { scoreEventForPostcode } from "../activities/whatsOn";
import { getPublicLocationAreaCode, getPublicLocationCode, resolveMemberCountryCode } from "./memberLocation";

type LocationHubMemberLike = {
  id: string;
  postcode?: string | null;
  country_code?: string | null;
};

type LocationHubEventLike = {
  id: string;
  location?: string | null;
  description?: string | null;
  locality_tags?: string[] | null;
  is_local_match?: boolean | null;
};

export type LocationCommunityHub<TMember extends LocationHubMemberLike, TEvent extends LocationHubEventLike> = {
  hasHub: boolean;
  outwardCode: string | null;
  label: string;
  members: TMember[];
  totalMembers: number;
  events: TEvent[];
  totalEvents: number;
};

export const getHubOutwardPostcode = (postcode: string | null | undefined, countryCode?: string | null) =>
  getPublicLocationCode(postcode, countryCode);

const getHubAreaToken = (postcode: string | null | undefined, countryCode?: string | null) =>
  getPublicLocationAreaCode(postcode, countryCode);

const sameOutwardPostcode = (
  viewer: { postcode?: string | null; countryCode?: string | null },
  member: LocationHubMemberLike,
) => {
  const viewerCountry = resolveMemberCountryCode(viewer.countryCode, viewer.postcode);
  const memberCountry = resolveMemberCountryCode(member.country_code, member.postcode);

  return Boolean(
    viewerCountry &&
      memberCountry &&
      viewerCountry === memberCountry &&
      getHubOutwardPostcode(viewer.postcode, viewerCountry) &&
      getHubOutwardPostcode(viewer.postcode, viewerCountry) === getHubOutwardPostcode(member.postcode, memberCountry),
  );
};

const sameAreaToken = (
  viewer: { postcode?: string | null; countryCode?: string | null },
  member: LocationHubMemberLike,
) => {
  const viewerCountry = resolveMemberCountryCode(viewer.countryCode, viewer.postcode);
  const memberCountry = resolveMemberCountryCode(member.country_code, member.postcode);

  return Boolean(
    viewerCountry === 'GB' &&
      memberCountry === 'GB' &&
      getHubAreaToken(viewer.postcode, viewerCountry) &&
      getHubAreaToken(viewer.postcode, viewerCountry) === getHubAreaToken(member.postcode, memberCountry),
  );
};

export const buildLocationCommunityHub = <
  TMember extends LocationHubMemberLike,
  TEvent extends LocationHubEventLike,
>(
  options: {
    postcode: string | null | undefined;
    countryCode?: string | null;
    members: TMember[];
    events: TEvent[];
    maxMembers?: number;
    maxEvents?: number;
  },
): LocationCommunityHub<TMember, TEvent> => {
  const outwardCode = getHubOutwardPostcode(options.postcode, options.countryCode);
  const areaToken = getHubAreaToken(options.postcode, options.countryCode);
  const viewer = { postcode: options.postcode, countryCode: options.countryCode };

  if (!outwardCode) {
    return {
      hasHub: false,
      outwardCode: null,
      label: "Your local hub",
      members: [],
      totalMembers: 0,
      events: [],
      totalEvents: 0,
    };
  }

  const exactMembers = options.members.filter((member) => sameOutwardPostcode(viewer, member));
  const fallbackMembers =
    exactMembers.length > 0 || !areaToken
      ? exactMembers
      : options.members.filter((member) => sameAreaToken(viewer, member));

  const exactEvents = options.events.filter((event) =>
    Boolean(event.is_local_match) ||
    scoreEventForPostcode(
      { location: event.location ?? null, description: event.description ?? null },
      options.postcode,
      options.countryCode,
    ) >= 100,
  );
  const fallbackEvents =
    exactEvents.length > 0 || !areaToken
      ? exactEvents
      : options.events.filter(
          (event) =>
            scoreEventForPostcode(
              { location: event.location ?? null, description: event.description ?? null },
              options.postcode,
              options.countryCode,
            ) >= 35,
        );

  const members = fallbackMembers.slice(0, options.maxMembers ?? 4);
  const events = fallbackEvents.slice(0, options.maxEvents ?? 4);

  return {
    hasHub: true,
    outwardCode,
    label: `Near ${outwardCode}`,
    members,
    totalMembers: fallbackMembers.length,
    events,
    totalEvents: fallbackEvents.length,
  };
};
